<template>
  <v-container class="parts-panel pa-0" fluid>
    <section class="parts-panel__toolbar">
      <v-text-field v-model="search" class="parts-panel__search" :label="$t('text.search')"
        prepend-inner-icon="mdi-magnify" clearable hide-details density="compact" variant="outlined">
      </v-text-field>

      <div v-if="parts && !loading" class="parts-panel__count">
        <span class="parts-panel__count-number">{{ filteredPartsCount }}</span>
        <span>{{ $t("text.Parts") }}</span>
      </div>
    </section>

    <v-virtual-scroll :bench="3" :items="filteredParts" :height="virtualPartsScrollHeight"
      :item-height="partItemHeight" class="parts-panel__scroll">
      <template v-slot="{ item }">
        <div v-if="item.empty" class="parts-panel__empty">
          <v-icon color="disabled" size="34">mdi-package-variant-closed</v-icon>
          <div class="parts-panel__empty-title">{{ emptyText }}</div>
        </div>

        <div v-else-if="item.loading" class="parts-panel__skeleton">
          <v-skeleton-loader class="parts-panel__skeleton-image" type="image" width="72" height="72">
          </v-skeleton-loader>
          <div class="parts-panel__skeleton-copy">
            <v-skeleton-loader type="text" width="70%"></v-skeleton-loader>
            <v-skeleton-loader type="text" width="38%"></v-skeleton-loader>
          </div>
          <v-skeleton-loader class="parts-panel__skeleton-action" type="button" width="132" height="36">
          </v-skeleton-loader>
        </div>

        <CommonPartListItem v-else :key="item.Id" :item="item" :machineId="machine.Id" :showPrice="showPrice"
          @click="showItem(item)" @addToCart="addToCart(item)">
        </CommonPartListItem>
      </template>
    </v-virtual-scroll>

    <router-view></router-view>
  </v-container>
</template>


<script>
import http from "@/api/client";
import CommonPartListItem from "@/components/ListItems/CommonPartListItem.vue";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";

export default {
  name: "PartsPanel",
  mixins: [AuthStoreMixin],
  components: {
    CommonPartListItem,
  },

  props: {
    machine: { type: Object, default: null },
  },

  data() {
    return {
      virtualPartsScrollHeight: 500,
      search: "",
      parts: null,
      loading: false,
    };
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    if (this.machine) this.updateParts(this.machine);
    else this.$emit("updateMachine");
    this.$nextTick(this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize() {
      const reservedHeight = this.isPhoneBool ? 280 : 292;
      this.virtualPartsScrollHeight = Math.max(window.innerHeight - reservedHeight, 280);
    },

    updateParts(machine) {
      if (!machine || !machine.MachineModel) { return; }
      this.loading = true;
      this.parts = null;
      http()
        .get("api2/customer/parts/" + machine.MachineModel.Id)
        .then((response) => {
          this.parts = response.data.map((part) => {
            return {
              ...part,
              Quantity: 1,
              CustomerArticlePrice: part.CustomerArticlePrice ?? null,
              HasCustomerPrice: !!part.HasCustomerPrice,
            };
          });
        })
        .catch(() => {
          this.parts = [];
        })
        .finally(() => {
          this.loading = false;
          this.onResize();
        });
    },

    showItem(item) {
      this.$router.push({
        name: "PartInfoDialog",
        params: { part: item.ArticleNumber },
      });
    },

    addToCart(item) {
      item.MachineId = this.machine.Id;
      this.addItemToCart(item);
    },

    superSearch(value, list) {
      if (!value || value.trim().length < 2 || !Array.isArray(list)) return [];

      const searchWords = value.trim().toLowerCase().split(" ").filter(Boolean);
      return list
        .map((part) => ({ part, points: this.getSearchPoints(part, searchWords) }))
        .filter((result) => result.points > 0)
        .sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          return (a.part.ArticleNumber || "").localeCompare(b.part.ArticleNumber || "");
        })
        .map((result) => result.part);
    },

    getSearchPoints(part, searchWords) {
      const haystack = `${part.ArticleNumber || ""} ${part.ArticleDescription || ""} ${part.SalesText || ""}`;
      const tokens = haystack.split(/\s+/).filter(Boolean);

      return searchWords.reduce((points, searchWord) => {
        return points + tokens.reduce((tokenPoints, token) => {
          const normalizedToken = token.toLowerCase();
          if (normalizedToken === searchWord) return tokenPoints + 2;
          if (normalizedToken.startsWith(searchWord)) return tokenPoints + 1;
          if (normalizedToken.includes(searchWord)) return tokenPoints + 0.25;
          return tokenPoints;
        }, 0);
      }, 0);
    },
  },

  computed: {
    filteredParts() {
      if (this.loading) return Array.from({ length: 6 }, (_, index) => ({ loading: true, Id: `loading-${index}` }));
      if (!this.parts) return [];
      if (this.parts.length === 0) return [{ empty: true }];
      if (this.normalizedSearch.length < 2) return this.parts;

      const res = this.superSearch(this.normalizedSearch, this.parts);
      if (res.length == 0) {
        return [{ empty: true }];
      }
      return res;
    },

    filteredPartsCount() {
      return this.filteredParts.filter((part) => !part.empty && !part.loading).length;
    },

    normalizedSearch() {
      return (this.search || "").trim();
    },

    emptyText() {
      if (this.normalizedSearch.length >= 2) return `${this.$t("text.no_results_for")} '${this.normalizedSearch}'`;
      return this.$t("text.none_available");
    },

    partItemHeight() {
      return this.isPhoneBool ? 154 : 104;
    },

    showPrice() {
      return this.canShowPrices;
    },
  },

  watch: {
    machine(val) {
      this.updateParts(val);
    },
  },
};
</script>

<style scoped>
.parts-panel {
  background: #f7f8fa;
  min-height: 100%;
}

.parts-panel__toolbar {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 2;
}

.parts-panel__search {
  max-width: 560px;
}

.parts-panel__count {
  align-items: baseline;
  color: #596170;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  margin-left: auto;
  white-space: nowrap;
}

.parts-panel__count-number {
  color: #1f2937;
  font-size: 18px;
}

.parts-panel__scroll {
  background: #ffffff;
}

.parts-panel__empty,
.parts-panel__skeleton {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  min-height: 104px;
  padding: 16px;
}

.parts-panel__empty {
  color: #596170;
  gap: 12px;
  justify-content: center;
}

.parts-panel__empty-title {
  font-size: 15px;
  font-weight: 600;
}

.parts-panel__skeleton {
  gap: 16px;
}

.parts-panel__skeleton-image {
  border-radius: 8px;
  overflow: hidden;
}

.parts-panel__skeleton-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.parts-panel__skeleton-action {
  flex: 0 0 auto;
}

@media (max-width: 640px) {
  .parts-panel__toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
  }

  .parts-panel__search {
    max-width: none;
  }

  .parts-panel__count {
    margin-left: 0;
  }

  .parts-panel__empty,
  .parts-panel__skeleton {
    min-height: 154px;
  }

  .parts-panel__skeleton {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
  }

  .parts-panel__skeleton-action {
    margin-left: 84px;
  }
}
</style>